import type { CalculationResult } from '@mortality/core';

const historyKey = (uid: string) => `history:${uid}`;
const pendingKey = (uid: string) => `history:pending:${uid}`;

type StoredCalculation = Omit<CalculationResult, 'timestamp'> & { timestamp: string };

export type PendingHistoryAddAction = {
  type: 'add';
  localId: string;
  calculation: Omit<CalculationResult, 'id' | 'timestamp'>;
  createdAt: number;
};

export type PendingHistoryDeleteAction = {
  type: 'delete';
  id: string;
  createdAt: number;
};

export type PendingHistoryClearAction = {
  type: 'clear';
  createdAt: number;
};

export type PendingHistoryAction =
  | PendingHistoryAddAction
  | PendingHistoryDeleteAction
  | PendingHistoryClearAction;

const serializeHistory = (entries: CalculationResult[]): StoredCalculation[] =>
  entries.map(entry => ({
    ...entry,
    timestamp: entry.timestamp.toISOString(),
  }));

const deserializeHistory = (entries: StoredCalculation[]): CalculationResult[] =>
  entries.map(entry => ({
    ...entry,
    timestamp: new Date(entry.timestamp),
  }));

const readItem = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(key);
};

const writeItem = (key: string, value: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (value == null) {
    window.localStorage.removeItem(key);
  } else {
    window.localStorage.setItem(key, value);
  }
};

export async function readCachedHistory(uid: string): Promise<CalculationResult[]> {
  const raw = readItem(historyKey(uid));
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as StoredCalculation[];
    return deserializeHistory(parsed);
  } catch (error) {
    console.warn('Failed to parse cached history for user', uid, error);
    writeItem(historyKey(uid), null);
    return [];
  }
}

export async function writeCachedHistory(uid: string, entries: CalculationResult[]): Promise<void> {
  writeItem(historyKey(uid), JSON.stringify(serializeHistory(entries)));
}

export async function clearCachedHistory(uid: string): Promise<void> {
  writeItem(historyKey(uid), null);
}

export async function getPendingHistoryActions(uid: string): Promise<PendingHistoryAction[]> {
  const raw = readItem(pendingKey(uid));
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw) as PendingHistoryAction[];
  } catch (error) {
    console.warn('Failed to parse pending history actions for user', uid, error);
    writeItem(pendingKey(uid), null);
    return [];
  }
}

export async function setPendingHistoryActions(uid: string, actions: PendingHistoryAction[]): Promise<void> {
  if (!actions.length) {
    writeItem(pendingKey(uid), null);
    return;
  }

  writeItem(pendingKey(uid), JSON.stringify(actions));
}

export async function appendPendingHistoryAction(uid: string, action: PendingHistoryAction): Promise<void> {
  const pending = await getPendingHistoryActions(uid);
  pending.push(action);
  await setPendingHistoryActions(uid, pending);
}

export async function removePendingAddAction(uid: string, localId: string): Promise<void> {
  const pending = await getPendingHistoryActions(uid);
  const filtered = pending.filter(
    entry => !(entry.type === 'add' && entry.localId === localId),
  );
  await setPendingHistoryActions(uid, filtered);
}

export async function clearPendingHistoryActions(uid: string): Promise<void> {
  writeItem(pendingKey(uid), null);
}
